import React, { useEffect, useState } from 'react';
import SVG from 'react-inlinesvg';
import Pagination from '../../../components/Pagination';
import Table from '../../../components/Table';
import useAlarm from '../../../context/AlarmContext';
import EditIcon from '../../../../../../assets/images/reskin-images/icon--edit.svg';
import EyeIcon from '../../../../../../assets/images/reskin-images/icon--eye.svg';
import BinIcon from '../../../../../../assets/images/reskin-images/icon--bin.svg';
import useModal from '../../../context/ModalContext';
import useAuth from '../../../context/AuthContext';
import FormLoader from '../../../components/FormLoader';
import DestroyModal from './DestroyModal';

const ActiveUsersTable = ({
  searchQuery,
  filter,
  items,
  onPaginationChange,
  checkedActiveUsers,
  setCheckedActiveUsers,
  setOpenSideActionBoard,
  checkedAllUsers,
  handleSelectAllUsers,
  totalUsers,
}) => {
  const [currentPage, setCurrentPage] = useState(
    items?.pagination?.current_page || 1
  );

  const { userData, isEmployee, isTenant } = useAuth();
  const { setAlarm } = useAlarm();
  const isAdmin = userData?.roles?.includes('admin');
  const hasAccess = isAdmin || isEmployee;

  useEffect(() => {
    onPaginationChange(currentPage);
  }, [currentPage]);

  const columns = [
    {
      label: 'Name',
      field: 'name',
      textAlign: 'left',
      fontWeight: 'semibold',
      width: '25%',
      minWidth: '100%'
    },
    {
      label: 'Email',
      field: 'email',
      textAlign: 'left',
      fontWeight: 'semibold',
      width: '35%',
    },
    {
      label: 'Access Type',
      field: 'access_type',
      textAlign: 'left',
      fontWeight: 'semibold',
      width: '15%',
    },
    {
      label: 'Cohorts',
      field: 'cohorts',
      textAlign: 'left',
      fontWeight: 'semibold',
      width: '15%',
    },
    {
      label: 'Sent by',
      field: 'sent_by',
      textAlign: 'left',
      fontWeight: 'semibold',
      width: '15%',
    },
    {
      label: 'Alex Expiration Date',
      field: 'alex_expiration_date',
      textAlign: 'center',
      width: '15%',
    },
    {
      label: 'User Expiration Date',
      field: 'user_expiration_date',
      textAlign: 'center',
      width: '15%',
    },
  ];

  const [isCVSLoading, setCVSIsLoading] = useState(false);

  const reconstructorFn = (user) => {
    const {
      id,
      email,
      created_at: createdAt,
      profile: { first_name: firstName, last_name: lastName },
      discounted_cents: discountedCents,
      application,
      access_type,
      alex_expiration_date,
      user_expiration_date,
      cohorts,
      invited_by_email: invitedByEmail,
      invited_by_name: invitedByName,
    } = user;

    const actionButtons = (
      <div className="flex h-full items-center shrink-0">
        <button
          type="button"
          className="flex items-center justify-content-center bg-purple-100 p-2 rounded-lg mr-2 hover:bg-purple-200"
          onClick={() => {
            setOpenSideActionBoard({
              type: 'Active Users',
              action: 'view',
              ids: [id],
              checkedAllUsers: checkedAllUsers,
              totalUsers: totalUsers,
            });
          }}
        >
          <SVG src={EyeIcon} />
        </button>
        <button
          type="button"
          className="flex items-center justify-content-center bg-purple-100 p-2 rounded-lg hover:bg-purple-200"
          onClick={() =>
            setOpenSideActionBoard({
              type: 'Active Users',
              action: 'edit',
              ids: [id],
              checkedAllUsers: checkedAllUsers,
              totalUsers: totalUsers,
            })
          }
        >
          <SVG src={EditIcon} />
        </button>
      </div>
    );

    return {
      id,
      name: (
        <div className="flex flex-no-wrap whitespace-no-wrap items-center justify-between">
          {firstName} {lastName} {isTenant ? '' : actionButtons}
        </div>
      ),
      email,
      application: id ? (application ?? '---') : '---',
      access_type: access_type ?? '---',
      cohorts: cohorts && cohorts.length > 0
        ? cohorts.map((cohort, index) => (
            <React.Fragment key={cohort.id}>
              {cohort.name}
              {index < cohorts.length - 1 && <br />}
            </React.Fragment>
          ))
        : '---',
      sent_by: invitedByName || invitedByEmail || '---',
      alex_expiration_date: formatDate(alex_expiration_date),
      user_expiration_date: formatDate(user_expiration_date), 
    };
  };

  const { setContent } = useModal();

  const editButtonLabel = checkedActiveUsers?.length >= 2 ? 'Users' : 'User';

  const formatDate = (dateString) => {
    if (!dateString) {
      return '---';
    }
    const options = {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      timeZone: 'UTC',
    };
    const date = new Date(dateString);
    const formattedDate = date.toLocaleDateString('en-US', options);
    return formattedDate.replace(/\//g, '-');
  };

  return (
    <>
      {hasAccess && (
        <div>
          <div className="mb-4 ml-10 flex items-center">
            <h2 className=" font-semibold">Active Users</h2>
            {checkedActiveUsers?.length >= 1 ? (
              <>
                {!isTenant && (
                  <button
                    onClick={() =>
                      setOpenSideActionBoard({
                        type: 'Active Users',
                        action: 'edit',
                        ids: checkedActiveUsers,
                        totalUsers: totalUsers,
                      })
                    }
                    type="button"
                    className="flex items-center font-semibold bg-purple-100 rounded-lg py-3 px-4 ml-8 hover:bg-purple-lightest"
                  >
                    <SVG src={EditIcon} className="mr-2" />
                    Edit {checkedAllUsers ? totalUsers.toLocaleString('en-US') : checkedActiveUsers.length} {editButtonLabel}
                  </button>
                )}
              </>
            ) : null}
            {checkedActiveUsers?.length ? (
              <button
                type="button"
                className="flex items-center font-semibold hover:bg-red-lighter rounded-lg p-3 ml-4"
                onClick={() => {
                  setContent({
                    content: (
                      <DestroyModal
                        items={checkedActiveUsers}
                        type="users"
                        queryListId="activeUsers"
                        setItems={setCheckedActiveUsers}
                        checkedAllUsers={checkedAllUsers}
                        totalUsers={totalUsers}
                        filter={filter}
                        searchQuery={searchQuery}
                        handleSelectAllUsers={handleSelectAllUsers}
                      />
                    ),
                  });
                }}
              >
                <SVG src={BinIcon} />
              </button>
            ) : null}
          </div>

          {!items?.isLoading && (
            <Table
              id="active_users"
              data={items?.users}
              columns={columns}
              reconstructorFn={reconstructorFn}
              checkedItems={checkedActiveUsers}
              setCheckedItems={setCheckedActiveUsers}
              totalCount={items?.pagination?.total_count}
              onSelectAllRecords={handleSelectAllUsers}
              checkedAllUsers={checkedAllUsers}
            />
          )}
          <Pagination
            currentPage={currentPage}
            totalPage={items?.pagination?.total_pages}
            onPageChange={(page) => setCurrentPage(page)}
          />
          {isCVSLoading && <FormLoader />}
        </div>
      )}
    </>
  );
};

export default ActiveUsersTable;
